import React, { useState } from "react";
import { PatternBackground } from "../../components/PatternBackground";
import { TwoTextCard } from "../../components/TwoTextCard";
import { ModalDialog } from "../../components/ModalDialog";
import {
  isFormCompleteIgnore,
  isBeneficiaryFormValid,
  isPercentageValid,
  isValidGhanaianNumber,
  notifyUser,
  validateGhanaCard,
} from "../../util/util";
import { serverRequest } from "../../util/server_request";
import { useNavigate } from "react-router-dom";
import { Spin, Modal } from "antd";

export const MemberNew = () => {
  const navigate = useNavigate();
  const currentDate = new Date().toISOString().split("T")[0];
  
  // Calculate max date of birth (must be at least 15 years old)
  const today = new Date();
  const minBirthDate = new Date(today.getFullYear() - 15, today.getMonth(), today.getDate()).toISOString().split("T")[0];

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [memberForm, setMemberForm] = useState({
    firstName: "",
    surname: "",
    ghanaCardNumber: "",
    dateOfBirth: "",
    gender: "",
    nationality: "Ghana",
    email: "",
    phoneNumber: "",
    ssnitNumber: "",
    salary: "",
    otherNames: ""
  });
  // otherNames: '',
  const [beneficiary, setBeneficiary] = useState({
    firstName: "",
    otherNames: "",
    mobileNumber: "",
    relationship: "",
    dateOfBirth: "",
    percentageAllocation: 0
  });
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const toggleModal = () => setIsOpen((prevState) => !prevState);
  const toggleLoading = () => setLoading((prevState) => !prevState);

  const memberHandler = (e) =>
    setMemberForm({ ...memberForm, [e.target.name]: e.target.value });
  const beneficiaryHandler = (e) =>
    setBeneficiary({ ...beneficiary, [e.target.name]: e.target.value });

  const changeDate = (e) => {
    setMemberForm({ ...memberForm, dateOfBirth: e.nativeEvent.target.value });
  };
  const changeDateBeneficiary = (e) => {
    setBeneficiary({ ...beneficiary, dateOfBirth: e.nativeEvent.target.value });
  };

  const addUser = () => {
    // Only validate Ghana Card if it's provided
    if (memberForm.ghanaCardNumber && memberForm.ghanaCardNumber.trim() !== "" && !validateGhanaCard(memberForm.ghanaCardNumber)) {
      return notifyUser(
        "error",
        "Ghana Card must be of format GHA-xxxxxxxxx-x",
        "Validation Error"
      );
    }
    if (!isValidGhanaianNumber(memberForm.phoneNumber)) {
      return notifyUser(
        "error",
        "Phone number must begin with 233 followed by 9 digits",
        "Validation Error"
      );
    }
    // Validate member is at least 15 years old
    if (memberForm.dateOfBirth && memberForm.dateOfBirth > minBirthDate) {
      return notifyUser(
        "error",
        "Member must be at least 15 years old",
        "Validation Error"
      );
    }
    // Only check percentage validity if beneficiaries are provided
    if (beneficiaries.length > 0 && !isPercentageValid(beneficiaries)) {
      return notifyUser(
        "error",
        "Beneficiaries percent allocation must sum up to 100%!",
        "Beneficiary Error"
      );
    }

    // Sanitize beneficiaries: remove OtherName, convert percentageAllocation to number, remove empty optional fields
    const sanitizedBeneficiaries = beneficiaries.map(b => {
      const { OtherName, ...rest } = b;
      const cleaned = { ...rest };
      // Convert percentageAllocation to number
      cleaned.percentageAllocation = Number(cleaned.percentageAllocation);
      // Remove empty identificationType and idNumber
      ["identificationType", "idNumber"].forEach(key => {
        if (cleaned[key] === "" || cleaned[key] === null || cleaned[key] === undefined) {
          delete cleaned[key];
        }
      });
      return cleaned;
    });


    // Sanitize memberForm: remove empty optional fields and add otherNames: 'Doe'
    const sanitizedMemberForm = { ...memberForm, otherNames: '' };
    Object.keys(sanitizedMemberForm).forEach(key => {
      if (sanitizedMemberForm[key] === "" || sanitizedMemberForm[key] === null || sanitizedMemberForm[key] === undefined) {
        // Only remove if truly optional, but here we keep all required fields and otherNames
        if (["ghanaCardNumber", "ssnitNumber", "firstName", "surname", "dateOfBirth", "gender", "nationality", "email", "phoneNumber", "salary", "otherNames"].includes(key)) return;
        delete sanitizedMemberForm[key];
      }
    });

    toggleLoading();
    serverRequest("POST", "members", { ...sanitizedMemberForm, beneficiaries: sanitizedBeneficiaries })
      .then((result) => {
        toggleLoading();
        if (!result?.success) {
          return notifyUser("error", result?.message, "Member creation failed");
        }
        notifyUser(
          "success",
          "Member created successfully!",
          "Member Creation"
        );
        navigate("/dashboard/members");
      })
      .catch((error) => {
        toggleLoading();
        notifyUser("error", error.message, "Member Creation");
      });
  };

  const confirmDeleteBeneficiary = (index) => {
    setDeleteIndex(index);
    setDeleteModalOpen(true);
  };

  const removeBeneficiary = () => {
    if (deleteIndex !== null) {
      setBeneficiaries((prev) => prev.filter((_, i) => i !== deleteIndex));
      setDeleteIndex(null);
    }
    setDeleteModalOpen(false);
  };

  const cancelDelete = () => {
    setDeleteIndex(null);
    setDeleteModalOpen(false);
  };

  const addBeneficiary = () => {
    if (!isBeneficiaryFormValid(beneficiary)) {
      return notifyUser(
        "error",
        "Required fields: First Name, Other Names, Relationship, Date of Birth, Mobile Number, and Percentage Allocation",
        "Validation Error",
        "topLeft"
      );
    }
    if (editingIndex !== null) {
      // Edit mode: update beneficiary at editingIndex
      setBeneficiaries((prev) => prev.map((b, idx) => idx === editingIndex ? { ...beneficiary } : b));
    } else {
      // Add mode: add new beneficiary
      setBeneficiaries((prev) => [
        ...prev,
        (({ OtherName, ...rest }) => rest)(beneficiary)
      ]);
    }
    setBeneficiary({
      firstName: "",
      otherNames: "",
      mobileNumber: "",
      relationship: "",
      dateOfBirth: "",
      percentageAllocation: 0
    });
    setEditingIndex(null);
    toggleModal();
  };

  return (
    <div className="">
      <PatternBackground>
        <p className="lead fw-bold mb-3">Create Member</p>
        <small>Create a new member</small>
      </PatternBackground>
      <div className="om-container">
        <Spin spinning={loading}>
          <div className="p-4 shadow-sm bg-white rounded">
            <p className="lead mt-5 mb-1">Create Member</p>
            <small>See al your pensions contributions</small>
            <div className="row mt-5">
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-text-field
                  label="First Name"
                  value={memberForm.firstName || ""}
                  type="text"
                  className="mb-4"
                  name="firstName"
                  onInput={memberHandler}
                />
              </div>
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-text-field
                  label="Last Name"
                  value={memberForm.surname || ""}
                  type="text"
                  className="mb-4"
                  name="surname"
                  onInput={memberHandler}
                />
              </div>
              {/*<div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">*/}
              {/*    <omk-text-field label="Other Name" value={memberForm.otherNames} type='text' className='mb-4' name='otherNames' onInput={memberHandler}/>*/}
              {/*</div>*/}
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-date-picker
                  label="Date of birth"
                  max={minBirthDate}
                  className="mb-4"
                  onInput={changeDate}
                  name="dateOfBirth"
                  value={memberForm.dateOfBirth || ""}
                  always-show-validation-message={
                    memberForm.dateOfBirth === ""
                      ? false
                      : memberForm.dateOfBirth > minBirthDate
                  }
                  custom-validation-message={
                    memberForm.dateOfBirth > minBirthDate
                      ? "Member must be at least 15 years old"
                      : ""
                  }
                />
              </div>
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-select
                  label="Gender"
                  value={memberForm.gender || ""}
                  onInput={memberHandler}
                  name="gender"
                  placeholder="Select Gender"
                >
                  <omk-select-option value="MALE">MALE</omk-select-option>
                  <omk-select-option value="FEMALE">FEMALE</omk-select-option>
                </omk-select>
              </div>
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-text-field
                  label="Email"
                  value={memberForm.email || ""}
                  type="email"
                  className="mb-4"
                  name="email"
                  onInput={memberHandler}
                />
              </div>
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-text-field
                  label="Nationality"
                  value={memberForm.nationality || ""}
                  type="text"
                  className="mb-4"
                  name="nationality"
                  onInput={memberHandler}
                />
              </div>
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-text-field
                  label="Phone Number"
                  value={memberForm.phoneNumber || ""}
                  type="text"
                  className="mb-4"
                  name="phoneNumber"
                  onInput={memberHandler}
                  maxlength="12"
                  always-show-validation-message={
                    memberForm.phoneNumber === ""
                      ? false
                      : !isValidGhanaianNumber(memberForm.phoneNumber)
                  }
                  custom-validation-message={
                    !isValidGhanaianNumber(memberForm.phoneNumber)
                      ? "Please enter number in format 233 followed by 9 digits without 0"
                      : ""
                  }
                />
              </div>
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-text-field
                  label="SSNIT Number"
                  value={memberForm.ssnitNumber || ""}
                  type="text"
                  className="mb-4"
                  name="ssnitNumber"
                  onInput={memberHandler}
                  maxlength="13"
                  always-show-validation-message={
                    memberForm.ssnitNumber === ""
                      ? false
                      : !/^(?=.*[a-zA-Z])[a-zA-Z0-9]{1,13}$/.test(memberForm.ssnitNumber)
                  }
                  custom-validation-message={
                    !/^(?=.*[a-zA-Z])[a-zA-Z0-9]{1,13}$/.test(memberForm.ssnitNumber)
                      ? "SSNIT number must be alphanumeric with max 13 chars and at least one letter"
                      : ""
                  }
                />
              </div>
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-text-field
                  label="Ghana Card Number"
                  value={memberForm.ghanaCardNumber || ""}
                  type="text"
                  className="mb-4"
                  name="ghanaCardNumber"
                  onInput={memberHandler}
                  always-show-validation-message={
                    memberForm.ghanaCardNumber === ""
                      ? true
                      : !validateGhanaCard(memberForm.ghanaCardNumber)
                  }
                  custom-validation-message={
                    memberForm.ghanaCardNumber === ""
                      ? "Ghana Card Number is required"
                      : !validateGhanaCard(memberForm.ghanaCardNumber)
                        ? "Ghana number should be GHA-xxxxxxxxx-x"
                        : ""
                  }
                />
              </div>
              <div className="col-lg-4 co-md-4 col-xs-12 col-sm-12 mb-4">
                <omk-text-field
                  label="Salary GH₵"
                  value={memberForm.salary || ""}
                  type="number"
                  className="mb-4"
                  name="salary"
                  onInput={memberHandler}
                />
              </div>
            </div>
            <div>
              <p className="lead mt-5">Add Beneficiaries</p>
              
            </div>
            <button
              className="btn btn-primary mt-3"
              onClick={toggleModal}
              disabled={beneficiaries.length >= 3}
            >
              Add Beneficiary <i className="bi bi-plus" />
            </button>
            <div className="row p-4 mt-4">
              {beneficiaries.map((b, index) => (
                <div
                  className="col-lg-4 col-md-4 col-sm-12 col-xs-12 mb-3"
                  key={index}
                >
                  <div className="shadow p-4 rounded">
                    <div className="row">
                      <div className="col">
                        <p className="fw-bold mb-0">{b.relationship}</p>
                      </div>
                      <div className="col">
                        <i
                          className="bi bi-pencil-square text-primary float-end me-2"
                          style={{ fontSize: "1.3rem", cursor: "pointer" }}
                          onClick={() => {
                            setBeneficiary({ ...b });
                            setEditingIndex(index);
                            setIsOpen(true);
                          }}
                        />
                        <i
                          className="bi bi-trash text-danger float-end"
                          style={{ fontSize: "1.3rem", cursor: "pointer" }}
                          onClick={() => confirmDeleteBeneficiary(index)}
                        />
                      </div>
                    </div>
                    <hr />
                    <TwoTextCard title={`${b.firstName} ${b.otherNames}`.trim()} subText="Full Name" />
                    <TwoTextCard
                      title={b.dateOfBirth}
                      subText="Date of Birth"
                    />
                    <TwoTextCard
                      title={b.identificationType}
                      subText="ID Type"
                    />
                    <TwoTextCard title={b.idNumber} subText="ID Number" />
                    <TwoTextCard
                      title={`${b.percentageAllocation}%`}
                      subText="Percent Allocation"
                    />
                    <hr />
                    <p className="fw-bold mb-0">
                      <i className="bi bi-telephone-fill" /> {b.mobileNumber}
                    </p>
                  </div>
                </div>
              ))}
            </div>
              <button
                className="btn btn-primary mt-3 w-25 shadow-sm"
                onClick={addUser}
                disabled={
                  !isFormCompleteIgnore(memberForm, "otherNames")
                }
              >
                Add Member
              </button>
          </div>
        </Spin>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onCancel={cancelDelete}
        footer={null}
        centered
        width={window.innerWidth <= 768 ? '90%' : 400}
        closable={false}
      >
        <div className="text-center p-3">
          <i
            className="bi bi-exclamation-triangle-fill text-warning mb-3"
            style={{ fontSize: "3rem" }}
          />
          <p className="lead">Are you sure?</p>
          <p className="text-muted">
            Do you really want to delete this beneficiary? This action cannot be undone.
          </p>
          <div className="d-flex gap-3 justify-content-center mt-4">
            <button className="btn btn-white" onClick={cancelDelete}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={removeBeneficiary}>
              Yes, Delete <i className="bi bi-trash" />
            </button>
          </div>
        </div>
      </Modal>

      <ModalDialog
        isOpen={isOpen}
        title="Add Beneficiary Details"
        closeFn={toggleModal}
      >
        <div className="">
          <div className="mb-4">
            <omk-select
              label="Relationship"
              value={beneficiary.relationship}
              onInput={beneficiaryHandler}
              name="relationship"
              placeholder="Select Relationship"
            >
              <omk-select-option value="Brother">Brother</omk-select-option>
              <omk-select-option value="Sister">Sister</omk-select-option>
              <omk-select-option value="Father">Father</omk-select-option>
              <omk-select-option value="Mother">Mother</omk-select-option>
              <omk-select-option value="Uncle">Uncle</omk-select-option>
            </omk-select>
          </div>
         
            <div className="mb-4">
            <omk-text-field
              label="Firstname"
              value={beneficiary.firstName || ""}
              type="text"
              className="mb-4"
              onInput={beneficiaryHandler}
              name="firstName"
            />
          </div>
           <div className="mb-4">
            <omk-text-field
              label="Othernames"
              value={beneficiary.otherNames || ""}
              type="text"
              className="mb-4"
              onInput={beneficiaryHandler}
              name="otherNames"
            />
          </div>
          <div className="mb-4">
            {/* <omk-text-field
              label="Date of birth"
              value={beneficiary.dateOfBirth}
              type="date"
              className="mb-4"
              name="dateOfBirth"
              onInput={beneficiaryHandler}
            /> */}
            <omk-date-picker
              label="Date of birth"
              max={currentDate}
              className="mb-4"
              onInput={changeDateBeneficiary}
              name="dateOfBirth"
              value={beneficiary.dateOfBirth || ""}
            />
          </div>
          <div className="mb-4">
            <omk-text-field
              label="Phone Number"
              value={beneficiary.mobileNumber || ""}
              type="text"
              className="mb-4"
              onInput={beneficiaryHandler}
              name="mobileNumber"
              maxlength="12"
              always-show-validation-message={
                beneficiary.mobileNumber === ""
                  ? false
                  : !isValidGhanaianNumber(beneficiary.mobileNumber)
              }
              custom-validation-message={
                !isValidGhanaianNumber(beneficiary.mobileNumber)
                  ? "Please enter number in format 233 followed by 9 digits without 0"
                  : ""
              }
            />
          </div>
          <div className="mb-4">
            <omk-select
              label="ID Type (Optional)"
              value={beneficiary.identificationType || ""}
              onInput={beneficiaryHandler}
              name="identificationType"
              placeholder="Select ID Type"
            >
              <omk-select-option value="NATIONAL_ID">
                Ghana Card
              </omk-select-option>
              <omk-select-option value="PASSPORT">Passport</omk-select-option>
              <omk-select-option value="VOTER_ID">Voter's ID</omk-select-option>
              <omk-select-option value="OTHER">Other</omk-select-option>
            </omk-select>
          </div>
          <div className="mb-4">
            <omk-text-field
              label="ID Number (Optional)"
              value={beneficiary.idNumber || ""}
              type="text"
              className="mb-4"
              onInput={beneficiaryHandler}
              name="idNumber"
            />
          </div>
          <div className="mb-4">
            <omk-text-field
              label="Percent Allocation"
              value={beneficiary.percentageAllocation || ""}
              type="number"
              className="mb-4"
              onInput={beneficiaryHandler}
              name="percentageAllocation"
              max="100"
            />
          </div>
          <button
            className="btn btn-primary mt-5 w-100"
            onClick={addBeneficiary}
          >
            Save Beneficiary <i className="bi bi-save" />
          </button>
        </div>
      </ModalDialog>
    </div>
  );
};
